import type {
  CopilotProposalStatus,
  CopilotProposalSummary,
} from "@repo/app-types/entities/copilotProposal";
import type { SignalProposalChange } from "@repo/app-types/entities/signalProposal";
import type { FC } from "react";
import { useLocation, useNavigate, type To } from "react-router";
import { useAppSelector } from "../../../hooks/hook.js";
import {
  ConfigurationProposalCard,
  ConfigurationProposalStatus,
} from "../../researchAgent/configurationProposalCard.js";
import { selectEditorProposalState } from "../../researchAgent/editorProposalsSlice.js";
import {
  buildSignalEditorPathname,
  buildSignalProposalEditLocation,
} from "./signalEditorUrlState.js";

export interface SignalProposalCardProps {
  proposal: CopilotProposalSummary<"signal">;
}

/**
 * A signal proposal in the chat. Review opens the signal editor with the
 * change in its draft, where Save applies it; Open shows the saved signal.
 * While the open editor holds the change, the card shows it as Viewing, then
 * Saving.
 */
export const SignalProposalCard: FC<SignalProposalCardProps> = (props) => {
  const { proposal } = props;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const editorState = useAppSelector((state) =>
    selectEditorProposalState(state, proposal.id),
  );
  const status =
    proposal.status === "pending" && editorState
      ? EDITOR_CARD_STATUSES[editorState]
      : CARD_STATUSES[proposal.status];
  const destination = buildDestination(proposal, status, pathname);

  return (
    <ConfigurationProposalCard
      resourceType="signal"
      operation={proposal.kind === "create_signal" ? "create" : "update"}
      resourceName={proposal.resource.name}
      summary={describeSignalProposalChange(proposal.change)}
      highlights={buildSignalProposalHighlights(proposal)}
      status={status}
      onAction={
        destination
          ? () => {
              void navigate(destination);
            }
          : undefined
      }
    />
  );
};

const CARD_STATUSES: Record<
  CopilotProposalStatus,
  ConfigurationProposalStatus
> = {
  pending: ConfigurationProposalStatus.Pending,
  applied: ConfigurationProposalStatus.Applied,
  discarded: ConfigurationProposalStatus.Outdated,
  stale: ConfigurationProposalStatus.Outdated,
};

const EDITOR_CARD_STATUSES = {
  viewing: ConfigurationProposalStatus.Viewing,
  saving: ConfigurationProposalStatus.Applying,
} as const;

/**
 * Where the card's action goes. A pending proposal opens the editor that puts
 * it into the draft: for a new signal, the new-signal editor, since nothing is
 * saved under the minted id until then, and it stays reviewable while its
 * editor is open. An applied one opens the signal it was saved to, unless that
 * signal is already open.
 */
function buildDestination(
  proposal: CopilotProposalSummary<"signal">,
  status: ConfigurationProposalStatus,
  currentPathname: string,
): To | undefined {
  switch (status) {
    case ConfigurationProposalStatus.Pending:
      return buildSignalProposalEditLocation({
        signalId:
          proposal.kind === "create_signal" ? undefined : proposal.resource.id,
        proposalId: proposal.id,
      });
    case ConfigurationProposalStatus.Applied: {
      const pathname = buildSignalEditorPathname(proposal.resource.id);
      return pathname === currentPathname ? undefined : { pathname };
    }
    case ConfigurationProposalStatus.Viewing:
    case ConfigurationProposalStatus.Applying:
    case ConfigurationProposalStatus.Outdated:
      return undefined;
  }
}

/** One sentence on what the signal would detect or what changes. */
function describeSignalProposalChange(change: SignalProposalChange): string {
  switch (change.kind) {
    case "create_signal":
      return change.description;
    case "update_signal":
      return change.change_summary;
  }
}

/**
 * The card's highlights: a new signal's field count, or the parts an update
 * changes, with its metadata counted by the fields added, removed, or edited.
 */
function buildSignalProposalHighlights(
  proposal: CopilotProposalSummary<"signal">,
): readonly string[] {
  const { change } = proposal;
  switch (change.kind) {
    case "create_signal":
      return change.metadata_schema.fields.length > 0
        ? [describeFieldCount(change.metadata_schema.fields.length)]
        : [];
    case "update_signal":
      return [
        change.name !== undefined ? "Name" : undefined,
        change.description !== undefined ? "Description" : undefined,
        change.metadata_schema
          ? proposal.changed_metadata_field_count !== undefined
            ? describeFieldCount(proposal.changed_metadata_field_count)
            : "Metadata"
          : undefined,
      ].filter((part) => part !== undefined);
  }
}

function describeFieldCount(count: number): string {
  return `${count} metadata field${count === 1 ? "" : "s"}`;
}
